import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Video, Mic, Square, Loader2, AlertCircle, Radio, Info, ExternalLink, Star, Shield } from "lucide-react";
import axios from "axios";
import { Tx } from "../context/AutoTranslateContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Chunk length for the live translator. 5 s is a good tradeoff between latency
// and Whisper accuracy — smaller = jittery translations, larger = laggy captions.
const CHUNK_MS = 5000;

// Keep all communication on-platform: redact phone numbers, emails and links
// from live captions so tutor/student can't swap private contact details.
const maskContact = (s) => {
  if (!s) return s;
  return s
    .replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, "[hidden]")
    .replace(/https?:\/\/\S+/gi, "[hidden]")
    .replace(/(?:\+?\d[\s-]?){7,}/g, "[hidden]");
};

const TutorSession = () => {
  const { sessionId } = useParams();
  const [params] = useSearchParams();
  const emailQ = params.get("email") || (() => {
    try { return localStorage.getItem("icc_academy_email") || ""; } catch { return ""; }
  })();

  const [session, setSession] = useState(null);
  const [error, setError] = useState("");
  const [emailInput, setEmailInput] = useState(emailQ);

  const [captioning, setCaptioning] = useState(false);
  const [captions, setCaptions] = useState([]);       // [{ar, en, t}]
  const [busyChunk, setBusyChunk] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [rateDone, setRateDone] = useState(false);
  const [rateErr, setRateErr] = useState("");
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunkTimerRef = useRef(null);

  const load = useCallback(async () => {
    if (!emailInput.trim()) { setSession(null); return; }
    try {
      const r = await axios.get(`${API}/tutoring/sessions/${sessionId}`, { params: { email: emailInput.trim() } });
      setSession(r.data); setError("");
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Could not load session");
      setSession(null);
    }
  }, [sessionId, emailInput]);
  useEffect(() => { if (emailQ) load(); /* eslint-disable-next-line */ }, []);

  // ── Live translation ───────────────────────────────────────────────
  const sendChunk = async (blob) => {
    if (!blob || blob.size < 1000) return;   // skip silent/tiny chunks
    setBusyChunk(true);
    try {
      const fd = new FormData();
      fd.append("audio", blob, "chunk.webm");
      fd.append("target_lang", "English");
      const r = await axios.post(`${API}/pronunciation/translate`, fd);
      if (r.data?.transcript || r.data?.translation) {
        setCaptions((c) => [...c, { ar: maskContact(r.data.transcript), en: maskContact(r.data.translation), t: new Date().toISOString() }].slice(-20));
      }
    } catch (e) {
      // a bad chunk shouldn't kill the whole caption stream
      console.debug("caption chunk skipped:", e);
    } finally { setBusyChunk(false); }
  };

  const startCaptions = async () => {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Rolling recorder — we start a new MediaRecorder every CHUNK_MS
      // so each chunk arrives as a complete, self-contained webm file
      // that Whisper can read without needing to buffer the whole session.
      let mr = null;
      const rotate = () => {
        if (mr && mr.state !== "inactive") mr.stop();
        mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
        recorderRef.current = mr;
        const chunks = [];
        mr.ondataavailable = (e) => e.data.size && chunks.push(e.data);
        mr.onstop = () => {
          const blob = new Blob(chunks, { type: "audio/webm" });
          sendChunk(blob);
        };
        mr.start();
      };
      rotate();
      chunkTimerRef.current = setInterval(rotate, CHUNK_MS);
      setCaptioning(true);
    } catch (e) {
      setError("Microphone access denied. Please allow the microphone to capture the tutor's voice.");
    }
  };
  const stopCaptions = () => {
    setCaptioning(false);
    if (chunkTimerRef.current) { clearInterval(chunkTimerRef.current); chunkTimerRef.current = null; }
    if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    saveTranscript();
  };
  // Archive the caption transcript so admin can review it later (disputes/quality).
  const saveTranscript = async () => {
    if (!session || captions.length === 0) return;
    try {
      await axios.post(`${API}/tutoring/sessions/${sessionId}/transcript`,
        { student_email: emailInput.trim(), lines: captions });
    } catch (e) { console.debug("transcript save failed:", e); }
  };
  const submitRating = async () => {
    setRateErr("");
    if (!rating) { setRateErr("Please pick 1–5 stars."); return; }
    try {
      await axios.post(`${API}/tutoring/sessions/${sessionId}/rate`,
        { rating, comment: comment.trim() || null, student_email: emailInput.trim() });
      setRateDone(true);
    } catch (e) { setRateErr(e.response?.data?.detail || e.message || "Could not submit rating"); }
  };
  useEffect(() => () => stopCaptions(), []);   // cleanup on unmount

  const scheduleForThisSession = session?.sessions?.[0];

  // Not paid → video URL is not shown by the API
  const canJoin = session?.payment_status === "paid" && session?.video_url;

  return (
    <div data-testid="tutor-session-page" className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
      <div className="flex items-center gap-2 text-xs text-mute mb-4">
        <Link to="/academy" className="hover:text-forest"><Tx>Academy</Tx></Link><span>/</span>
        <Link to="/academy/tutors" className="hover:text-forest"><Tx>Tutors</Tx></Link><span>/</span>
        <span className="text-forest"><Tx>Live session</Tx></span>
      </div>

      {/* Email prompt — session is only accessible with the buyer's email */}
      {!session && (
        <div className="max-w-md mx-auto clay-surface rounded-3xl p-6 space-y-3">
          <p className="text-sm text-mute">
            <Tx>Enter the email you used to book this session:</Tx>
          </p>
          <input
            data-testid="session-email"
            type="email"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            placeholder="you@example.com"
            className="w-full px-4 py-2.5 rounded-full bg-cream border border-forest/10 focus:border-forest outline-none text-sm"
          />
          <button data-testid="session-load" onClick={load}
            className="w-full py-2.5 bg-forest text-sand rounded-full hover:bg-sage text-sm">
            <Tx>Open my session</Tx>
          </button>
          {error && <p className="text-xs text-clay bg-clay/10 rounded-full px-3 py-2">{error}</p>}
        </div>
      )}

      {session && (
        <div className="grid lg:grid-cols-[1fr_360px] gap-6">
          {/* Left — video */}
          <div className="clay-surface rounded-3xl overflow-hidden">
            {canJoin ? (
              <>
                <div className="bg-forest text-sand px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Video className="w-4 h-4" />
                    <span className="text-sm font-medium"><Tx>Tutor session</Tx> · {session.tutor_name}</span>
                  </div>
                  <a href={session.video_url} target="_blank" rel="noopener noreferrer"
                    className="text-xs inline-flex items-center gap-1 text-gold hover:underline">
                    <ExternalLink className="w-3 h-3" /> <Tx>Open in new tab</Tx>
                  </a>
                </div>
                <iframe
                  data-testid="session-jitsi"
                  title="Jitsi video"
                  allow="camera; microphone; fullscreen; display-capture; autoplay"
                  src={session.video_url}
                  className="w-full"
                  style={{ height: "70vh", minHeight: 500, border: 0 }}
                />
              </>
            ) : (
              <div className="p-10 text-center">
                <Video className="w-10 h-10 text-mute mx-auto mb-3" />
                <p className="text-sm text-mute mb-1"><Tx>The video link unlocks after payment is confirmed.</Tx></p>
                <p className="text-xs text-mute">
                  <Tx>Status:</Tx> <strong>{session.payment_status}</strong>
                </p>
              </div>
            )}
          </div>

          {/* Right — live captions */}
          <div className="clay-surface rounded-3xl p-5 flex flex-col" style={{ maxHeight: "78vh" }}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Radio className={`w-4 h-4 ${captioning ? "text-clay animate-pulse" : "text-gold"}`} />
                <h3 className="font-heading text-forest font-semibold text-sm"><Tx>Live captions (AR → EN)</Tx></h3>
              </div>
              {!captioning ? (
                <button data-testid="captions-start" onClick={startCaptions}
                  className="text-xs inline-flex items-center gap-1 bg-forest text-sand px-3 py-1.5 rounded-full hover:bg-sage">
                  <Mic className="w-3 h-3" /> <Tx>Start</Tx>
                </button>
              ) : (
                <button data-testid="captions-stop" onClick={stopCaptions}
                  className="text-xs inline-flex items-center gap-1 bg-clay text-sand px-3 py-1.5 rounded-full">
                  <Square className="w-3 h-3 fill-current" /> <Tx>Stop</Tx>
                </button>
              )}
            </div>

            {/* Info notice */}
            <div className="flex items-start gap-2 text-[11px] text-mute bg-cream rounded-2xl p-3 mb-3">
              <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <p>
                <Tx>Best-effort AI translation using OpenAI Whisper + a small language model. Not a substitute for a human interpreter — Qur'anic terms may be simplified.</Tx>
              </p>
            </div>

            {/* Keep-it-on-platform notice */}
            <div className="flex items-start gap-2 text-[11px] text-forest bg-gold/10 border border-gold/30 rounded-2xl p-3 mb-3">
              <Shield className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gold" />
              <p>
                <Tx>For your safety, please keep all communication and payment inside the platform. Phone numbers, emails and links are automatically hidden from the captions.</Tx>
              </p>
            </div>

            {/* Rolling captions */}
            <div className="flex-1 overflow-y-auto space-y-3 pr-1" data-testid="captions-list">
              {captions.length === 0 && (
                <p className="text-xs text-mute italic text-center py-8">
                  {captioning ? <Tx>Listening… captions will appear here.</Tx> : <Tx>Press Start when your tutor begins speaking.</Tx>}
                </p>
              )}
              {captions.map((c, i) => (
                <div key={i} className="border-b border-forest/5 pb-2">
                  {c.ar && <p className="arabic text-base text-forest text-right" dir="rtl">{c.ar}</p>}
                  {c.en && <p className="text-xs text-ink mt-1"><em>{c.en}</em></p>}
                </div>
              ))}
              {busyChunk && <p className="text-[10px] text-mute italic text-center"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /><Tx>translating…</Tx></p>}
            </div>

            {error && (
              <div className="flex items-start gap-2 text-xs text-clay bg-clay/10 rounded-full px-3 py-2 mt-2">
                <AlertCircle className="w-3.5 h-3.5 mt-0.5" /> <span>{error}</span>
              </div>
            )}

            {/* Post-session rating */}
            {canJoin && (
              <div data-testid="rating-card" className="mt-4 border-t border-forest/10 pt-4">
                <p className="text-[10px] uppercase tracking-widest text-gold mb-2"><Tx>Rate your tutor</Tx></p>
                {rateDone ? (
                  <p data-testid="rating-done" className="text-xs text-forest inline-flex items-center gap-1">
                    <Star className="w-3.5 h-3.5 fill-current text-gold" /> <Tx>Thank you — your review was submitted.</Tx>
                  </p>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button key={n} type="button" data-testid={`star-${n}`} onClick={() => setRating(n)}
                          className="p-0.5" aria-label={`${n} stars`}>
                          <Star className={`w-6 h-6 ${n <= rating ? "fill-current text-gold" : "text-mute"}`} />
                        </button>
                      ))}
                    </div>
                    <textarea data-testid="rating-comment" value={comment} onChange={(e) => setComment(e.target.value)}
                      rows={2} maxLength={500} placeholder="Optional review…"
                      className="w-full px-3 py-2 rounded-2xl bg-cream border border-forest/10 outline-none focus:border-forest text-xs" />
                    <button data-testid="rating-submit" onClick={submitRating}
                      className="w-full py-2 bg-forest text-sand rounded-full text-xs hover:bg-sage">
                      <Tx>Submit review</Tx>
                    </button>
                    {rateErr && <p className="text-[11px] text-clay">{rateErr}</p>}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TutorSession;
